/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  dynamicFormService,
  type CreateDynamicFormPayload,
  type GetDynamicFormParams,
  type UpdateDynamicFormPayload,
} from "@src/@api-services/dynamic-form";
import type { IDynamicForm } from "../types";
import { mapApiForm } from "../helpers";
import { TableSort } from "@src/shared/constants/sort-order.constants";

interface DynamicFormsState {
  data: IDynamicForm[];
  total: number;
  params: GetDynamicFormParams;
  selectedForm: IDynamicForm | null;
  templates: { id: string | number; key: string; name: string }[];
  loading: boolean;
  detailLoading: boolean;
  error: string | null;
}

export const getDynamicForms = createAsyncThunk(
  "dynamicForms/getDynamicForms",
  async (params: GetDynamicFormParams, { rejectWithValue }) => {
    try {
      const response = await dynamicFormService.getDynamicForms(params);
      return {
        params,
        data: (response.data.data.data || []).map(mapApiForm),
        total: response.data.data.totalRecord || 0,
      };
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch forms"
      );
    }
  }
);

export const getDynamicForm = createAsyncThunk(
  "dynamicForms/getDynamicForm",
  async (id: string | number, { rejectWithValue }) => {
    try {
      const response = await dynamicFormService.getDynamicForm(id);
      return mapApiForm(response.data.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch form"
      );
    }
  }
);

export const getDynamicFormTemplates = createAsyncThunk(
  "dynamicForms/getDynamicFormTemplates",
  async (_, { rejectWithValue }) => {
    try {
      const response = await dynamicFormService.getDynamicFormTemplates();
      const rows = response.data?.data?.data ?? response.data?.data ?? [];
      return Array.isArray(rows) ? rows : [];
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch templates"
      );
    }
  }
);

export const createDynamicForm = createAsyncThunk(
  "dynamicForms/createDynamicForm",
  async (data: CreateDynamicFormPayload, { rejectWithValue }) => {
    try {
      const response = await dynamicFormService.createDynamicForm(data);
      return mapApiForm(response.data.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create form"
      );
    }
  }
);

export const updateDynamicForm = createAsyncThunk(
  "dynamicForms/updateDynamicForm",
  async (
    { id, data }: { id: string | number; data: UpdateDynamicFormPayload },
    { rejectWithValue }
  ) => {
    try {
      const response = await dynamicFormService.updateDynamicForm(id, data);
      return mapApiForm(response.data.data);
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to update form"
      );
    }
  }
);

export const deleteDynamicForm = createAsyncThunk(
  "dynamicForms/deleteDynamicForm",
  async (id: string | number, { rejectWithValue }) => {
    try {
      await dynamicFormService.deleteDynamicForm(id);
      return id;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to delete form"
      );
    }
  }
);

const initialState: DynamicFormsState = {
  data: [],
  total: 0,
  params: {
    page: 1,
    pageSize: 10,
    orderBy: "createdAt",
    orderDirection: TableSort.DESC,
  },
  selectedForm: null,
  templates: [],
  loading: false,
  detailLoading: false,
  error: null,
};

export const dynamicFormsSlice = createSlice({
  name: "dynamicForms",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearSelectedForm: (state) => {
      state.selectedForm = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getDynamicForms.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDynamicForms.fulfilled, (state, action) => {
        state.loading = false;
        state.data = action.payload.data;
        state.total = action.payload.total;
        state.params = action.payload.params;
      })
      .addCase(getDynamicForms.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        state.data = [];
        state.total = 0;
      })
      .addCase(getDynamicForm.pending, (state) => {
        state.detailLoading = true;
        state.error = null;
      })
      .addCase(getDynamicForm.fulfilled, (state, action) => {
        state.detailLoading = false;
        state.selectedForm = action.payload;
      })
      .addCase(getDynamicForm.rejected, (state) => {
        state.detailLoading = false;
        state.selectedForm = null;
      })
      .addCase(getDynamicFormTemplates.fulfilled, (state, action) => {
        state.templates = action.payload;
      });
  },
});

export const { clearError, clearSelectedForm } = dynamicFormsSlice.actions;
export default dynamicFormsSlice.reducer;
